import { CartUpdatedEvent } from '@theme/events';

/**
 * Bouton code promo : un clic copie le code et l'applique au panier, sans quitter la page.
 * Si l'appel échoue, on suit le lien /discount/CODE du snippet, qui fait la même chose côté Shopify.
 * Un code appliqué sur un panier vide est gardé par Shopify et s'applique au premier ajout.
 */
class PromoCodeBouton extends HTMLElement {
  connectedCallback() {
    this.code = (this.dataset.code || '').trim();
    this.lien = this.querySelector('.pcb__bouton');
    this.annonce = this.querySelector('.pcb__annonce');
    if (!this.code || !this.lien) return;

    this.lien.setAttribute('role', 'button');
    this.surClic = (event) => this.appliquer(event);
    this.lien.addEventListener('click', this.surClic);
    this.lireEtat();
  }

  disconnectedCallback() {
    if (this.lien && this.surClic) this.lien.removeEventListener('click', this.surClic);
  }

  get racine() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  memeCode(autre) {
    return String(autre || '').toUpperCase() === this.code.toUpperCase();
  }

  etat(nom, message) {
    this.dataset.etat = nom;
    this.lien.setAttribute('aria-busy', String(nom === 'charge'));
    if (message && this.annonce) this.annonce.textContent = message;
  }

  /* Le code est peut-être déjà dans le panier (appliqué sur une autre page) : on l'affiche. */
  async lireEtat() {
    try {
      const reponse = await fetch(`${this.racine}cart.js`, { headers: { Accept: 'application/json' } });
      if (!reponse.ok) return;
      const panier = await reponse.json();
      const vide = !panier.item_count;
      if ((panier.discount_codes || []).some((d) => this.memeCode(d.code) && (d.applicable !== false || vide))) this.etat('ok');
    } catch (erreur) {
      /* Lecture facultative : la pastille reste sur le code. */
    }
  }

  async appliquer(event) {
    if (this.dataset.etat === 'charge') {
      event.preventDefault();
      return;
    }
    if (this.dataset.etat === 'ok') {
      event.preventDefault();
      this.etat('ok', `Le code ${this.code} est déjà appliqué au panier.`);
      return;
    }
    event.preventDefault();
    this.etat('charge');

    if (navigator.clipboard) navigator.clipboard.writeText(this.code).catch(() => {});

    try {
      const panier = await (await fetch(`${this.racine}cart.js`, { headers: { Accept: 'application/json' } })).json();
      const autres = (panier.discount_codes || []).map((d) => d.code).filter((c) => !this.memeCode(c));

      const sections = [];
      const tiroir = document.querySelector('cart-drawer');
      if (tiroir && tiroir.dataset.sectionId) sections.push(tiroir.dataset.sectionId);

      const url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url) || `${this.racine}cart/update`;
      const reponse = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: [...autres, this.code].join(','), sections: sections.join(',') }),
      });
      if (!reponse.ok) throw new Error(`cart/update ${reponse.status}`);
      const donnees = await reponse.json();

      const ligne = (donnees.discount_codes || []).find((d) => this.memeCode(d.code));
      if (!ligne) throw new Error('code absent du panier');

      if (ligne.applicable === false && donnees.item_count > 0) {
        this.etat(
          'erreur',
          autres.length
            ? `Le code ${this.code} ne se cumule pas avec le code ${autres.join(', ')} déjà présent dans votre panier.`
            : `Le code ${this.code} ne s'applique pas à ce panier.`,
        );
        return;
      }

      const message =
        donnees.item_count > 0
          ? `Code ${this.code} appliqué au panier.`
          : `Code ${this.code} enregistré : il s'appliquera dès que vous ajouterez un produit.`;
      document.querySelectorAll('promo-code-bouton').forEach((bouton) => {
        if (bouton !== this && bouton.code && this.memeCode(bouton.code)) bouton.etat('ok');
      });
      this.etat('ok', message);

      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
      } else {
        document.dispatchEvent(new CartUpdatedEvent({}, { source: 'promo-code-bouton', sections: donnees.sections }));
      }
    } catch (erreur) {
      /* Repli : le lien /discount/CODE applique le code et revient sur cette page. */
      window.location.href = this.lien.href;
    }
  }
}

if (!customElements.get('promo-code-bouton')) customElements.define('promo-code-bouton', PromoCodeBouton);
